import React, {useState} from "react";
import {checkUsername, registerAccount} from "../../../api/accountApi";
import {useTranslation} from "react-i18next";
import PasswordRules from "../../components/PasswordRules";
import {passwordRules} from "../../../utils/passwordRules";
import {MessageType} from "../../components/MessageBox";

interface RegisterFormProps {
    setMessage: (message: MessageType) => void;
    switchToLogin: () => void;
}

export default function RegisterForm({setMessage, switchToLogin}: RegisterFormProps) {
    const {t} = useTranslation();

    const [form, setForm] = useState({
        email: "",
        username: "",
        password: "",
        repeatPassword: "",
        firstName: "",
        lastName: ""
    });

    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);

    const passwordValid = passwordRules.every((r) => r.test(form.password));
    const passwordsMatch =
        form.password === form.repeatPassword && form.password !== "" && form.repeatPassword !== "";

    const allFilled =
        form.email.trim() &&
        form.username.trim() &&
        form.password.trim() &&
        form.repeatPassword.trim() &&
        form.firstName.trim() &&
        form.lastName.trim();

    const isValid = Boolean(allFilled && passwordValid && passwordsMatch && usernameAvailable === true);

    const handleUsernameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setForm({...form, username: value});

        if (value.length > 2) {
            try {
                const exists = await checkUsername(value);
                setUsernameAvailable(!exists);
            } catch {
                setUsernameAvailable(null);
            }
        } else {
            setUsernameAvailable(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid) return;

        try {
            const {repeatPassword, ...dto} = form;
            const accountObj = await registerAccount(dto);

            setMessage({type: "success", text: t("auth.messages.registerSuccess", {username: accountObj.username})});
            switchToLogin();
        } catch (err: any) {
            setMessage({type: "error", text: err?.message || t("auth.messages.registerError")});
            // setError(err?.message || t("auth.messages.registerError"));
        }
    };

    return (
        <form onSubmit={handleSubmit} className="form-container auth-form scrollbar-area" autoComplete="off">
            <div className="scrollbar-area">
                <div className="form-group">
                    <label>{t("auth.fields.email")}</label>
                    <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({...form, email: e.target.value})}
                        className="form-input"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>{t("auth.fields.username")}</label>
                    <div className="input-with-icon">
                        <input
                            type="text"
                            value={form.username}
                            onChange={handleUsernameChange}
                            autoComplete="off"
                            className="form-input"
                            required
                        />
                        {usernameAvailable === true && <span className="icon icon-green">✔</span>}
                        {usernameAvailable === false && <span className="icon icon-red">✖</span>}
                    </div>
                </div>

                <div className="form-group">
                    <label>{t("contact.fields.firstName")}</label>
                    <input
                        type="text"
                        value={form.firstName}
                        onChange={(e) => setForm({...form, firstName: e.target.value})}
                        className="form-input"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>{t("contact.fields.lastName")}</label>
                    <input
                        type="text"
                        value={form.lastName}
                        onChange={(e) => setForm({...form, lastName: e.target.value})}
                        className="form-input"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>{t("auth.fields.password")}</label>
                    <input
                        type="password"
                        value={form.password}
                        onChange={(e) => setForm({...form, password: e.target.value})}
                        autoComplete="new-password"
                        className="form-input"
                        required
                    />

                    <PasswordRules password={form.password}/>
                </div>

                <div className="form-group">
                    <label>{t("auth.fields.repeatPasswordField")}</label>
                    <input
                        type="password"
                        value={form.repeatPassword}
                        onChange={(e) => setForm({...form, repeatPassword: e.target.value})}
                        autoComplete="new-password"
                        className="form-input"
                        required
                    />

                    {form.repeatPassword.length > 0
                        && !passwordsMatch && form.repeatPassword.trim() !== "" && (
                            <small className="error-text">{t("auth.messages.passwordMismatch")}</small>
                        )}
                </div>
            </div>

            <button type="submit" className="form-button" disabled={!isValid}>
                {t("auth.actions.register")}
            </button>
        </form>
    );
}